import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap, timeout } from 'rxjs/operators';
import { PagesService } from './pages.service';
import { PublicApi } from '../constants';

/**
 * CRUD actions a role can be granted on a single page.
 * `view` gates the other three: without it the page is not reachable at all.
 *
 * NOTE: `field` is the exact key sent to / read from the backend. If the API
 * uses different names (e.g. `can_update` instead of `can_edit`), change it
 * here only — the whole UI and payload are driven off this list.
 */
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';

export interface PermissionActionDef {
  key: PermissionAction;
  field: 'can_view' | 'can_create' | 'can_edit' | 'can_delete';
  label: string;
  icon: string;
  hint: string;
}

export const PERMISSION_ACTIONS: PermissionActionDef[] = [
  { key: 'view', field: 'can_view', label: 'View', icon: 'fa-solid fa-eye', hint: 'Open the page and read its data' },
  { key: 'create', field: 'can_create', label: 'Create', icon: 'fa-solid fa-plus', hint: 'Add new records on this page' },
  { key: 'edit', field: 'can_edit', label: 'Edit', icon: 'fa-solid fa-pen-to-square', hint: 'Update existing records' },
  { key: 'delete', field: 'can_delete', label: 'Delete', icon: 'fa-solid fa-trash-can', hint: 'Remove records permanently' },
];

// NOTE: brand colour tokens live in branding-tokens.ts — kept out of this file so the
// permissions service stays about permissions.

export interface RolePermissionPageItem {
  page_id: number;
  page_name: string;
  page_key: string;
  url: string;
  parent_id: number | null;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  [key: string]: any;
}

export interface ClientRolePermissionsResponse {
  role: {
    id: number;
    policy_name: string;
    description?: string;
  };
  allowed_count: number;
  total_pages: number;
  pages: RolePermissionPageItem[];
}

/**
 * Role permissions rendered over the live `client_pages` list.
 * `usedFallback` is true only when GET /client_pages itself gave nothing back — the
 * ids then come from the local placeholder list and must not be saved.
 */
export interface RolePagePermissionsResult extends ClientRolePermissionsResponse {
  usedFallback: boolean;
}

/**
 * One shared snapshot of the Policy Management chain, so the Pages, Roles,
 * Roles Management and User Management screens can never disagree about how many
 * pages, roles or assigned sub-users exist.
 */
export interface PolicySummary {
  pagesCount: number;
  rolesCount: number;
  subUsersCount: number;
  /** Sub-users that have a client role, so they can actually reach something. */
  assignedCount: number;
  /** Sub-users with no role — they are denied everything until one is assigned. */
  unassignedCount: number;
  /** role id -> number of sub-users carrying it. */
  usersPerRole: Record<number, number>;
  /** role id -> number of pages the role grants view on. */
  pagesPerRole: Record<number, number>;
  loadedAt: number;
}

export interface MyPermissionsResponse {
  is_sub_user: boolean;
  role: string;
  source: 'role' | 'direct' | 'owner' | string;
  assigned_role: { id: number; name: string } | null;
  allowed_keys: string[];
  pages: RolePermissionPageItem[];
}

export interface ClientRoleItem {
  id: number;
  policy_name: string;
  description?: string;
  created_at?: number | string;
  permissions_count?: number;
  assigned_users_count?: number;
  [key: string]: any;
}

// Backward compatibility interfaces for existing codebase
export interface ClientPageItem {
  id?: number;
  page_id?: number;
  name?: string;
  page_name?: string;
  title?: string;
  slug?: string;
  page_key?: string;
  route?: string;
  url?: string;
  parent_id?: number | null;
  group?: string;
  category?: string;
  icon?: string;
  description?: string;
  can_view?: boolean;
}

export interface SubuserPermissionItem {
  id?: number;
  page_id?: number;
  client_pages_id?: number;
  sub_user_id?: number;
  page_name?: string;
  slug?: string;
  page_key?: string;
  route?: string;
  url?: string;
  group?: string;
  can_view: boolean;
  page?: ClientPageItem;
}

@Injectable({
  providedIn: 'root',
})
export class SubuserPermissionsService {
  private pagesService = inject(PagesService);
  private http = inject(HttpClient);

  // Cached state of current logged-in user's permissions
  private myPermissionsSubject = new BehaviorSubject<MyPermissionsResponse | null>(null);
  public myPermissions$ = this.myPermissionsSubject.asObservable();

  // Cached list of all client portal pages
  private cachedClientPages: ClientPageItem[] | null = null;

  // One shared Policy Management snapshot (pages -> roles -> permissions -> users)
  private policySummarySubject = new BehaviorSubject<PolicySummary | null>(null);
  public policySummary$ = this.policySummarySubject.asObservable();
  private policySummaryInFlight: Observable<PolicySummary> | null = null;

  constructor() {
    this.restoreCachedPermissions();
  }

  private restoreCachedPermissions(): void {
    try {
      const stored = localStorage.getItem('luqta_my_permissions');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed.is_sub_user === 'boolean') {
          this.myPermissionsSubject.next(parsed);
        }
      }
    } catch (e) {
      console.warn('[SubuserPermissionsService] Error restoring cached permissions:', e);
    }
  }

  hasNoPermissions(): boolean {
    const cached = this.myPermissionsSubject.value;
    if (!cached || !cached.is_sub_user) return false;
    return !cached.allowed_keys || cached.allowed_keys.length === 0;
  }

  getClientPages(forceRefresh = false): Observable<ClientPageItem[]> {
    if (!forceRefresh && this.cachedClientPages && this.cachedClientPages.length > 0) {
      return of(this.cachedClientPages);
    }
    return this.pagesService.getAll('CLIENT_PAGES').pipe(
      timeout(3500),
      map((res: any) => {
        const list = Array.isArray(res) ? res : (res?.data || res?.items || res?.result || []);
        let normalized: ClientPageItem[] = list.map((item: any) => ({
          id: item.id ?? item.page_id,
          page_id: item.id ?? item.page_id,
          name: item.name ?? item.page_name,
          page_name: item.name ?? item.page_name,
          slug: item.slug ?? item.page_key,
          page_key: item.page_key ?? item.slug,
          route: item.route ?? item.url,
          url: item.url ?? item.route,
          parent_id: item.parent_id ?? null,
          group: item.group || 'General',
          can_view: item.can_view ?? true,
        }));
        if (!normalized || normalized.length === 0) {
          normalized = this.getFallbackClientPages();
        }
        this.cachedClientPages = normalized;
        return normalized;
      }),
      catchError(() => {
        const fallback = this.getFallbackClientPages();
        this.cachedClientPages = fallback;
        return of(fallback);
      })
    );
  }

  // ─────────────────────────────────────────────────────────────
  // client_pages CRUD (Policy Management > Pages)
  // ─────────────────────────────────────────────────────────────

  /** GET /client_pages — raw list (no fallback), used by the Pages CRUD screen. */
  getClientPagesRaw(): Observable<ClientPageItem[]> {
    return this.pagesService.getAll('CLIENT_PAGES').pipe(
      timeout(6000),
      map((res: any) => {
        const list = Array.isArray(res) ? res : (res?.data || res?.items || res?.result || []);
        return list.map((item: any) => ({
          id: Number(item.id ?? item.page_id),
          page_id: Number(item.id ?? item.page_id),
          page_name: item.page_name ?? item.name ?? '',
          name: item.page_name ?? item.name ?? '',
          page_key: item.page_key ?? item.slug ?? '',
          slug: item.page_key ?? item.slug ?? '',
          url: item.url ?? item.route ?? '',
          route: item.url ?? item.route ?? '',
          parent_id: item.parent_id ? Number(item.parent_id) : null,
        }) as ClientPageItem);
      }),
      tap((list) => {
        // keep the sidebar cache in sync with what the admin sees
        if (list.length > 0) this.cachedClientPages = list;
      })
    );
  }

  /** POST /client_pages — body: { page_name, page_key, url, parent_id } (no id). */
  createClientPage(page: { page_name: string; page_key: string; url: string; parent_id: number }): Observable<any> {
    const payload = {
      page_name: page.page_name.trim(),
      page_key: page.page_key.trim(),
      url: page.url.trim(),
      parent_id: Number(page.parent_id) || 0,
    };
    console.log('[SubuserPermissionsService] POST /client_pages payload:', payload);
    return this.pagesService.create('CLIENT_PAGES', payload).pipe(
      tap(() => { this.cachedClientPages = null; })
    );
  }

  /**
   * POST /client_pages — body: { id, page_name, page_key, url, parent_id }.
   *
   * The same endpoint creates and updates: sending a real `id` makes the backend
   * update that row and reply with action "updated" (id 0 / absent creates).
   * There is no PUT route — it answers 404 "Unable to locate request".
   */
  updateClientPage(page: { id: number; page_name: string; page_key: string; url: string; parent_id: number }): Observable<any> {
    const payload = {
      id: Number(page.id),
      page_name: page.page_name.trim(),
      page_key: page.page_key.trim(),
      url: page.url.trim(),
      parent_id: Number(page.parent_id) || 0,
    };
    console.log('[SubuserPermissionsService] POST (update) /client_pages payload:', payload);
    return this.pagesService.create('CLIENT_PAGES', payload).pipe(
      tap(() => { this.cachedClientPages = null; })
    );
  }

  /** DELETE /client_pages — body: { id }. */
  deleteClientPage(id: number | string): Observable<any> {
    const payload = { id: Number(id) };
    console.log('[SubuserPermissionsService] DELETE /client_pages payload:', payload);
    return this.pagesService.deleteWithBody('CLIENT_PAGES', payload).pipe(
      tap(() => { this.cachedClientPages = null; })
    );
  }

  /**
   * Page permissions for one role, built on the real page list.
   *
   * GET /client_pages decides WHICH pages exist (and their real ids, keys, routes
   * and parent_id), while GET /client_role_permissions only supplies this role's
   * flags. That way a page added on the Pages screen shows up here immediately, and
   * every page_id sent back to /client_role_permissions_bulk is a genuine row id.
   *
   * Flags are matched on page_key first (the stable identifier) and on page_id
   * second, so the two endpoints disagreeing about ids cannot drop a grant.
   */
  getRolePagePermissions(roleId: number | string): Observable<RolePagePermissionsResult> {
    const numId = Number(roleId);

    return forkJoin({
      pages: this.getClientPagesRaw().pipe(
        timeout(8000),
        catchError((err) => {
          console.warn('[SubuserPermissionsService] GET /client_pages failed for role permissions:', err);
          return of([] as ClientPageItem[]);
        })
      ),
      perms: this.getClientRolePermissions(numId).pipe(
        catchError(() => of(null as ClientRolePermissionsResponse | null))
      ),
    }).pipe(
      map(({ pages, perms }) => {
        const normKey = (k: any) => String(k || '').toLowerCase().trim().replace(/-/g, '_');

        // Flags from the role endpoint, indexed both ways.
        const byKey = new Map<string, RolePermissionPageItem>();
        const byId = new Map<number, RolePermissionPageItem>();
        for (const p of perms?.pages || []) {
          const k = normKey(p.page_key);
          if (k) byKey.set(k, p);
          if (p.page_id) byId.set(Number(p.page_id), p);
        }

        const usedFallback = !pages || pages.length === 0;
        const source: ClientPageItem[] = usedFallback ? this.getFallbackClientPages() : pages;
        if (usedFallback) {
          console.warn('[SubuserPermissionsService] /client_pages returned no rows — showing the placeholder list, saving is unsafe.');
        }

        const merged: RolePermissionPageItem[] = source.map((cp) => {
          const id = Number(cp.id ?? cp.page_id);
          const key = normKey(cp.page_key ?? cp.slug);
          const flags = (key && byKey.get(key)) || byId.get(id);
          const canView = flags?.can_view === true;

          return {
            page_id: id,
            page_name: cp.page_name || cp.name || `Page #${id}`,
            page_key: cp.page_key || cp.slug || '',
            url: cp.url || cp.route || '',
            parent_id: cp.parent_id !== undefined && cp.parent_id !== null ? Number(cp.parent_id) || null : null,
            can_view: canView,
            // A page the role cannot open can never carry the other three.
            can_create: canView && flags?.can_create === true,
            can_edit: canView && flags?.can_edit === true,
            can_delete: canView && flags?.can_delete === true,
          };
        });

        merged.sort((a, b) => a.page_id - b.page_id);

        return {
          role: perms?.role || { id: numId, policy_name: `Role #${numId}` },
          allowed_count: merged.filter((p) => p.can_view).length,
          total_pages: merged.length,
          pages: merged,
          usedFallback,
        } as RolePagePermissionsResult;
      })
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Shared Policy Management snapshot
  // ─────────────────────────────────────────────────────────────

  /**
   * The parent (company admin) id every Policy Management screen must query with.
   * Centralised because three screens used to keep their own copy of this
   * fallback, so a missing userDetails could point them at different accounts.
   */
  getEffectiveParentId(): number {
    let id = 0;
    try {
      const raw = localStorage.getItem('userDetails');
      if (raw) {
        const parsed = JSON.parse(raw);
        id = Number(parsed?.id || parsed?.user_id || 0);
      }
    } catch {
      // userDetails is stored encrypted — callers pass the decrypted id instead.
    }
    return id > 0 ? id : 62;
  }

  /**
   * Loads the whole chain once and shares it: how many pages exist, how many
   * roles, how many sub-users, which of them have a role, and per role both the
   * user count and the granted-page count.
   *
   * Every screen renders from this, so the Roles list and Roles Management can no
   * longer show different counts for the same role.
   */
  getPolicySummary(parentId?: number, forceRefresh = false): Observable<PolicySummary> {
    const current = this.policySummarySubject.value;
    if (!forceRefresh && current) return of(current);
    if (!forceRefresh && this.policySummaryInFlight) return this.policySummaryInFlight;

    const parent = parentId && parentId > 0 ? parentId : this.getEffectiveParentId();

    const request = forkJoin({
      pages: this.getClientPagesRaw().pipe(timeout(8000), catchError(() => of([] as ClientPageItem[]))),
      roles: this.getClientRoles().pipe(catchError(() => of([] as ClientRoleItem[]))),
      subUsers: this.getSubUsersByParent(parent, 1, 200).pipe(catchError(() => of({ items: [] }))),
    }).pipe(
      switchMap(({ pages, roles, subUsers }) => {
        const list: any[] = subUsers?.items || subUsers?.data || (Array.isArray(subUsers) ? subUsers : []);

        const usersPerRole: Record<number, number> = {};
        let assignedCount = 0;
        for (const u of list) {
          const roleId = this.getSubUserAssignedRole(u, roles);
          if (roleId > 0) {
            usersPerRole[roleId] = (usersPerRole[roleId] || 0) + 1;
            assignedCount++;
          }
        }

        // Granted-page count per role, read from the same endpoint the
        // permissions grid saves to, so the number always matches the toggles.
        const perRole = roles.length
          ? forkJoin(
              roles.map((r) =>
                this.getClientRolePermissions(r.id).pipe(
                  map((res) => ({ id: Number(r.id), granted: (res?.pages || []).filter((p) => p.can_view).length })),
                  catchError(() => of({ id: Number(r.id), granted: 0 }))
                )
              )
            )
          : of([] as Array<{ id: number; granted: number }>);

        return perRole.pipe(
          map((counts) => {
            const pagesPerRole: Record<number, number> = {};
            for (const c of counts) pagesPerRole[c.id] = c.granted;

            const summary: PolicySummary = {
              pagesCount: pages.length,
              rolesCount: roles.length,
              subUsersCount: list.length,
              assignedCount,
              unassignedCount: Math.max(0, list.length - assignedCount),
              usersPerRole,
              pagesPerRole,
              loadedAt: Date.now(),
            };
            return summary;
          })
        );
      }),
      tap((summary) => {
        this.policySummarySubject.next(summary);
        this.policySummaryInFlight = null;
        console.log('[SubuserPermissionsService] Policy summary:', summary);
      }),
      catchError((err) => {
        console.warn('[SubuserPermissionsService] Failed to build policy summary:', err);
        this.policySummaryInFlight = null;
        const empty: PolicySummary = {
          pagesCount: 0, rolesCount: 0, subUsersCount: 0, assignedCount: 0,
          unassignedCount: 0, usersPerRole: {}, pagesPerRole: {}, loadedAt: Date.now(),
        };
        return of(empty);
      }),
      shareReplay(1)
    );

    this.policySummaryInFlight = request;
    return request;
  }

  /**
   * Call after anything in the chain changes — a page added, a role created or
   * deleted, permissions saved, a role assigned — so every screen picks up the
   * new numbers instead of showing a stale count.
   */
  refreshPolicySummary(parentId?: number): void {
    this.getPolicySummary(parentId, true).subscribe({
      error: () => { /* summary is best-effort, screens still render */ },
    });
  }

  getSubuserPermissions(subUserId: number): Observable<SubuserPermissionItem[]> {
    return this.pagesService.getAll('SUBUSER_PERMISSIONS', { sub_user_id: subUserId }).pipe(
      timeout(3500),
      map((res: any) => {
        const list = Array.isArray(res) ? res : (res?.pages || res?.data || res?.items || res?.result || []);
        return list.map((item: any) => ({
          id: item.id ?? item.page_id,
          page_id: item.page_id ?? item.client_pages_id,
          sub_user_id: item.sub_user_id ?? subUserId,
          page_name: item.page_name ?? item.name,
          slug: item.slug ?? item.page_key,
          page_key: item.page_key ?? item.slug,
          can_view: item.can_view === true || item.can_view === 1 || item.can_view === 'true' || item.can_view === '1',
        }));
      }),
      catchError(() => of([]))
    );
  }

  getSubuserPermissionsRaw(subUserId: number): Observable<any> {
    return this.pagesService.getAll('SUBUSER_PERMISSIONS', { sub_user_id: subUserId }).pipe(
      timeout(5000),
      catchError(() => of(null))
    );
  }

  updateSubuserPermissionsBulk(subUserId: number, permissions: any[]): Observable<any> {
    const payload = {
      sub_user_id: Number(subUserId),
      permissions: permissions.map((p) => ({
        page_id: p.page_id ?? p.id,
        can_view: !!p.can_view,
        ...(p.slug ? { slug: p.slug } : {}),
      })),
    };
    return this.pagesService.update('SUBUSER_PERMISSIONS_BULK', payload);
  }


  /**
   * 1. GET /my_permissions
   * Called on login for every user on the client portal.
   * Returns:
   * {
   *   "is_sub_user": true,
   *   "role": "company_sub_user",
   *   "source": "role",
   *   "assigned_role": { "id": 5, "name": "Manager" },
   *   "allowed_keys": ["engagements", "participants"],
   *   "pages": [ { "page_id": 1, "page_name": "Engagements", "page_key": "engagements", "url": "/home/contests", "parent_id": null, "can_view": true } ]
   * }
   */
  getMyPermissions(forceRefresh = false): Observable<MyPermissionsResponse | null> {
    if (!forceRefresh && this.myPermissionsSubject.value !== null) {
      return of(this.myPermissionsSubject.value);
    }

    console.log('[SubuserPermissionsService] Fetching GET /my_permissions...');
    return this.pagesService.getAll('MY_PERMISSIONS').pipe(
      timeout(4500),
      map((res: any) => {
        console.log('[SubuserPermissionsService] GET /my_permissions response:', res);
        const data = res?.data || res?.result || res;
        const normalized: MyPermissionsResponse = {
          is_sub_user: data?.is_sub_user === true,
          role: data?.role || (data?.is_sub_user ? 'company_sub_user' : 'company_admin'),
          source: data?.source || (data?.is_sub_user ? 'role' : 'owner'),
          assigned_role: data?.assigned_role || null,
          allowed_keys: Array.isArray(data?.allowed_keys) ? data.allowed_keys : [],
          pages: Array.isArray(data?.pages)
            ? data.pages.map((p: any) => ({
                page_id: Number(p.page_id ?? p.id),
                page_name: p.page_name ?? p.name ?? '',
                page_key: p.page_key ?? p.slug ?? '',
                url: p.url ?? p.route ?? '',
                parent_id: p.parent_id !== undefined && p.parent_id !== null ? Number(p.parent_id) : null,
                can_view: p.can_view === true,
                can_create: p.can_create === true,
                can_edit: (p.can_edit ?? p.can_update) === true,
                can_delete: p.can_delete === true,
              }))
            : [],
        };

        this.myPermissionsSubject.next(normalized);
        try {
          localStorage.setItem('luqta_my_permissions', JSON.stringify(normalized));
        } catch (e) {
          // ignore quota error
        }

        return normalized;
      }),
      catchError((err) => {
        console.warn('[SubuserPermissionsService] Failed to fetch /my_permissions:', err);
        return of(this.myPermissionsSubject.value);
      })
    );
  }

  /**
   * Synchronous check whether current logged in user can access a given route or page_key.
   * - is_sub_user: false -> company_admin, everything is allowed (no gating).
   * - is_sub_user: true -> DEFAULT DENY. Only allowed if page_key is in allowed_keys.
   */
  /**
   * True when `base` is the page that owns `candidate` — the same route, or a
   * parent segment of it, so /home/contests covers /home/contests/web-qr.
   *
   * The match has to land on a "/" boundary. A plain startsWith() made /home (the
   * Dashboard page) cover every /home/* route, so a sub-user granted only Dashboard
   * was allowed into the entire app; it also made "roles" cover "roles-management".
   * The /home root is therefore exact-match only.
   */
  private urlCovers(base: string, candidate: string): boolean {
    const b = (base || '').replace(/\/+$/, '');
    const c = (candidate || '').replace(/\/+$/, '');
    if (!b || !c) return false;
    if (b === c) return true;
    if (b === '/home') return false;
    return c.startsWith(`${b}/`);
  }

  canAccess(routeOrKey: string, explicitIsSubUser?: boolean): boolean {
    const cached = this.myPermissionsSubject.value;
    const isSub = explicitIsSubUser !== undefined ? explicitIsSubUser : (cached?.is_sub_user ?? this.detectIsSubUser());

    // Company admin has full unrestricted access
    if (!isSub) {
      return true;
    }

    // DEFAULT DENY: If sub-user has no permissions loaded or empty allowed_keys, deny
    if (!cached || !Array.isArray(cached.allowed_keys) || cached.allowed_keys.length === 0) {
      return false;
    }

    const cleanInput = (routeOrKey || '').trim();
    const cleanTarget = cleanInput.replace(/^\/home\/?/, '').replace(/^\//, '').toLowerCase().trim();

    // 1. Direct match on page_key in allowed_keys
    if (cached.allowed_keys.includes(cleanInput) || cached.allowed_keys.includes(cleanTarget)) {
      return true;
    }

    // 2. Match against pages list by url or route.
    // The MOST SPECIFIC page wins: an exact page_key hit first, then the longest
    // covering url. Taking the first match instead let a parent decide a child's
    // access (granting Engagements silently granted Web QR).
    if (cached.pages && cached.pages.length > 0) {
      const lowerInput = cleanInput.toLowerCase();
      const scored = cached.pages
        .map((p) => {
          const pUrl = (p.url || '').toLowerCase().trim();
          const pCleanUrl = pUrl.replace(/^\/home\/?/, '').replace(/^\//, '');
          let score = -1;
          if (p.page_key && p.page_key.toLowerCase() === cleanTarget) score = Math.max(score, 1000);
          if (this.urlCovers(pUrl, lowerInput)) score = Math.max(score, pUrl.length);
          if (this.urlCovers(pCleanUrl, cleanTarget)) score = Math.max(score, pCleanUrl.length);
          return { page: p, score };
        })
        .filter((x) => x.score >= 0)
        .sort((a, b) => b.score - a.score);

      if (scored.length > 0) {
        const best = scored[0].page;
        return cached.allowed_keys.includes(best.page_key) && best.can_view === true;
      }
    }

    // 3. Fallback standard URL-to-page_key mapping
    const mappedKey = this.mapUrlToPageKey(cleanInput);
    if (mappedKey && cached.allowed_keys.includes(mappedKey)) {
      return true;
    }

    return false;
  }

  /**
   * Synchronous CRUD check for a page: `canDo('contests', 'create')`.
   * Same rules as canAccess() — company admins get everything, sub-users are
   * default-deny — plus the page must be viewable before create/edit/delete count.
   *
   * Use it to hide/disable action buttons, e.g.
   *   <button *ngIf="perms.canDo('contests', 'delete')">Delete</button>
   */
  canDo(routeOrKey: string, action: PermissionAction, explicitIsSubUser?: boolean): boolean {
    if (action === 'view') {
      return this.canAccess(routeOrKey, explicitIsSubUser);
    }

    const cached = this.myPermissionsSubject.value;
    const isSub = explicitIsSubUser !== undefined ? explicitIsSubUser : (cached?.is_sub_user ?? this.detectIsSubUser());
    if (!isSub) return true;

    // Must be able to open the page before any action on it is possible.
    if (!this.canAccess(routeOrKey, isSub)) return false;

    const page = this.findPermissionPage(routeOrKey);
    if (!page) return false;

    const def = PERMISSION_ACTIONS.find((a) => a.key === action);
    return def ? page[def.field] === true : false;
  }

  /** Resolve a route or page_key to the cached permission row for the current user. */
  private findPermissionPage(routeOrKey: string): RolePermissionPageItem | null {
    const cached = this.myPermissionsSubject.value;
    if (!cached?.pages?.length) return null;

    const cleanInput = (routeOrKey || '').trim().toLowerCase();
    const cleanTarget = cleanInput.replace(/^\/home\/?/, '').replace(/^\//, '').trim();

    return (
      cached.pages.find((p) => {
        const pUrl = (p.url || '').toLowerCase().trim();
        const pCleanUrl = pUrl.replace(/^\/home\/?/, '').replace(/^\//, '');
        if ((p.page_key || '').toLowerCase() === cleanTarget) return true;
        if (pUrl && (pUrl === cleanInput || cleanInput.startsWith(pUrl))) return true;
        if (pCleanUrl && (pCleanUrl === cleanTarget || cleanTarget.startsWith(pCleanUrl))) return true;
        return false;
      }) || null
    );
  }

  /**
   * Get the first allowed URL for a sub-user to redirect when an unauthorized URL is typed directly.
   */
  getFirstAllowedUrl(): string {
    const cached = this.myPermissionsSubject.value;
    if (!cached || !cached.is_sub_user) {
      return '/home';
    }

    if (cached.pages && cached.pages.length > 0) {
      const firstPage = cached.pages.find((p) => p.can_view === true && !!p.url);
      if (firstPage && firstPage.url) {
        return firstPage.url;
      }
    }

    if (cached.allowed_keys && cached.allowed_keys.length > 0) {
      for (const key of cached.allowed_keys) {
        const url = this.mapKeyToUrl(key);
        if (url) return url;
      }
    }

    return '/home/profile';
  }

  private detectIsSubUser(): boolean {
    try {
      const stored = localStorage.getItem('userDetails');
      if (stored) {
        const user = JSON.parse(stored);
        return user?.is_sub_user === true || !!user?.sub_user_id;
      }
    } catch {
      // ignore
    }
    return false;
  }

  /**
   * Route -> page_key. Keys are snake_case and must match the backend
   * `client_pages.page_key` values exactly; sub-routes fold into their parent page
   * so e.g. /home/contests/edit is still gated by the `engagements` key.
   */
  private mapUrlToPageKey(url: string): string {
    const clean = url.replace(/^\/home\/?/, '').replace(/^\//, '').toLowerCase().trim();
    const map: Record<string, string> = {
      '': 'dashboard',
      'dashboard': 'dashboard',
      'quick-actions': 'quick_actions',
      'ai-insight': 'ai_insights',

      'contests': 'engagements',
      'contests/add': 'engagements',
      'contests/edit': 'engagements',
      'contests/detail': 'engagements',
      'contests/monitoring': 'engagements',
      'engagements': 'engagements',
      'quiz': 'quiz',
      'participants': 'participants',
      'contests/web-qr': 'web_qr',
      'web-qr': 'web_qr',
      'branches': 'branches',

      'developer': 'developer',
      'sdk-documentation': 'sdk_documentation',
      'sdk-applications': 'sdk_applications',
      'activity-logs': 'activity_logs',
      'whatsapp-logs': 'whatsapp_logs',
      'whatsapp': 'whatsapp_conversation_log',
      // Legacy URL, before the route was shortened to /home/whatsapp.
      'whatsapp-conversation-log': 'whatsapp_conversation_log',
      'branding': 'branding',
      '3d-model': 'model_3d',

      'subscription': 'subscription',
      'billing': 'billing',
      'user-managment': 'user_management',
      'policy-management': 'policy_management',
      'policy-management/roles': 'roles',
      'policy-management/roles-management': 'roles_management',
      'roles': 'roles',
      'roles-management': 'roles_management',
      'policy-management/client-pages': 'pages',
      'client-pages': 'pages',
      'profile': 'settings',
      'updates': 'updates',
    };
    return map[clean] || clean.replace(/-/g, '_');
  }

  /** page_key -> route. Inverse of mapUrlToPageKey(); legacy kebab keys still resolve. */
  private mapKeyToUrl(key: string): string {
    const clean = (key || '').toLowerCase().trim();
    const map: Record<string, string> = {
      'dashboard': '/home',
      'quick_actions': '/home/quick-actions',
      'ai_insights': '/home/ai-insight',

      'engagements': '/home/contests',
      'quiz': '/home/quiz',
      'participants': '/home/participants',
      'web_qr': '/home/contests/web-qr',
      'branches': '/home/branches',

      'developer': '/home/developer',
      'sdk_documentation': '/home/sdk-documentation',
      'sdk_applications': '/home/sdk-applications',
      'activity_logs': '/home/activity-logs',
      'whatsapp_logs': '/home/whatsapp-logs',
      'whatsapp_conversation_log': '/home/whatsapp',
      'branding': '/home/branding',
      'model_3d': '/home/3d-model',

      'subscription': '/home/subscription',
      'billing': '/home/billing',
      'user_management': '/home/user-managment',
      'policy_management': '/home/policy-management',
      'roles': '/home/roles',
      'roles_management': '/home/roles-management',
      'pages': '/home/client-pages',
      'settings': '/home/profile',
      'updates': '/home/updates',
    };
    return map[clean] || `/home/${clean.replace(/_/g, '-')}`;
  }

  /**
   * 2. GET /client_roles
   * Fetches the roles list: [ { "id": 5, "policy_name": "Manager", "description": "..." } ]
   */
  getClientRoles(): Observable<ClientRoleItem[]> {
    console.log('[SubuserPermissionsService] Fetching GET /client_roles...');
    return this.pagesService.getAll('CLIENT_ROLES').pipe(
      timeout(4500),
      map((res: any) => {
        console.log('[SubuserPermissionsService] GET /client_roles response:', res);
        const list = Array.isArray(res) ? res : (res?.data || res?.items || res?.result || []);
        return list.map((r: any) => ({
          id: Number(r.id ?? r.role_id),
          policy_name: r.policy_name || r.name || `Role #${r.id}`,
          description: r.description || '',
          permissions_count: r.permissions_count,
          assigned_users_count: r.assigned_users_count,
          created_at: r.created_at,
        }));
      }),
      catchError((err) => {
        console.warn('[SubuserPermissionsService] Failed to fetch /client_roles:', err);
        return of([]);
      })
    );
  }

  /**
   * 3. POST /client_roles
   * Creates a new role: { "policy_name": "Manager", "description": "Reports + participants" }
   * Note: Duplicate name returns an error message -> caller displays inline.
   */
  createClientRole(policyName: string, description: string): Observable<any> {
    const payload = {
      policy_name: policyName.trim(),
      description: description.trim(),
    };
    console.log('[SubuserPermissionsService] POST /client_roles payload:', payload);
    return this.pagesService.create('CLIENT_ROLES', payload).pipe(
      tap((res) => console.log('[SubuserPermissionsService] POST /client_roles success:', res))
    );
  }

  /**
   * 3b. PATCH /client_roles/{id} or POST /client_roles
   * Updates an existing role name and description
   */
  updateClientRole(roleId: number | string, policyName: string, description: string): Observable<any> {
    const id = Number(roleId);
    const payload = {
      id,
      role_id: id,
      policy_name: policyName.trim(),
      description: description.trim(),
    };
    console.log(`[SubuserPermissionsService] Updating role ${id} with:`, payload);

    return this.pagesService.patch('CLIENT_ROLES', id, payload).pipe(
      catchError(() => this.pagesService.update('CLIENT_ROLES', payload)),
      catchError(() => this.http.put(`${PublicApi.CLIENT_ROLES}/${id}`, payload)),
      catchError(() => this.http.post(`${PublicApi.CLIENT_ROLES}/${id}`, payload)),
      tap((res) => console.log(`[SubuserPermissionsService] updateClientRole success:`, res))
    );
  }

  /**
   * 3c. DELETE /client_roles/{id}
   * Deletes a role
   */
  deleteClientRole(roleId: number | string): Observable<any> {
    const id = Number(roleId);
    console.log(`[SubuserPermissionsService] Deleting role ${id}...`);

    return this.pagesService.deleteCard('CLIENT_ROLES', id).pipe(
      catchError(() => this.http.delete(`${PublicApi.CLIENT_ROLES}?role_id=${id}`)),
      catchError(() => this.http.delete(`${PublicApi.CLIENT_ROLES}?id=${id}`)),
      catchError(() => this.pagesService.deleteWithBody('CLIENT_ROLES', { id, role_id: id })),
      tap((res) => console.log(`[SubuserPermissionsService] deleteClientRole success:`, res))
    );
  }

  /**
   * 4. GET /client_role_permissions?role_id=5
   * Response:
   * {
   *   "role": { "id": 5, "policy_name": "Manager" },
   *   "allowed_count": 2,
   *   "total_pages": 6,
   *   "pages": [
   *     { "page_id": 1, "page_name": "Engagements", "page_key": "engagements", "url": "/home/contests", "parent_id": null, "can_view": true },
   *     { "page_id": 2, "page_name": "Participants", "page_key": "participants", "url": "/home/participants", "parent_id": null, "can_view": false }
   *   ]
   * }
   */
  getClientRolePermissions(roleId: number | string): Observable<ClientRolePermissionsResponse> {
    const numId = Number(roleId);
    console.log(`[SubuserPermissionsService] GET /client_role_permissions?role_id=${numId}...`);
    return this.pagesService.getAll('CLIENT_ROLE_PERMISSIONS', { role_id: numId }).pipe(
      timeout(4500),
      map((res: any) => {
        console.log(`[SubuserPermissionsService] GET /client_role_permissions response for role ${numId}:`, res);
        const data = res?.data || res?.result || res;
        let pagesList = Array.isArray(data?.pages)
          ? data.pages
          : Array.isArray(data)
          ? data
          : [];

        // If backend returned empty pages list, fallback to standard client pages list
        if (!pagesList || pagesList.length === 0) {
          pagesList = this.getFallbackClientPages().map((cp) => ({
            page_id: cp.id || cp.page_id || 1,
            page_name: cp.name || cp.page_name || '',
            page_key: cp.page_key || cp.slug || '',
            url: cp.route || cp.url || '',
            parent_id: cp.parent_id !== undefined ? cp.parent_id : null,
            can_view: false,
            can_create: false,
            can_edit: false,
            can_delete: false,
          }));
        }

        const normalizedPages: RolePermissionPageItem[] = pagesList.map((p: any) => ({
          page_id: Number(p.page_id ?? p.id),
          page_name: p.page_name ?? p.name ?? `Page #${p.page_id ?? p.id}`,
          page_key: p.page_key ?? p.slug ?? '',
          url: p.url ?? p.route ?? '',
          parent_id: p.parent_id !== undefined && p.parent_id !== null ? Number(p.parent_id) : null,
          can_view: p.can_view === true,
          // Older API responses only carry can_view — treat the CRUD flags as denied
          // unless the backend explicitly sends them.
          can_create: p.can_create === true,
          can_edit: (p.can_edit ?? p.can_update) === true,
          can_delete: p.can_delete === true,
        }));

        const calculatedAllowed = normalizedPages.filter((p) => p.can_view).length;
        const totalCount = (typeof data?.total_pages === 'number' && data.total_pages > 0)
          ? data.total_pages
          : normalizedPages.length;

        const response: ClientRolePermissionsResponse = {
          role: data?.role || { id: numId, policy_name: `Role #${numId}` },
          allowed_count: typeof data?.allowed_count === 'number' && data.allowed_count >= 0
            ? (data.allowed_count > 0 ? data.allowed_count : calculatedAllowed)
            : calculatedAllowed,
          total_pages: totalCount,
          pages: normalizedPages,
        };

        return response;
      }),
      catchError((err) => {
        console.warn(`[SubuserPermissionsService] Failed to fetch permissions for role ${numId}:`, err);
        // Fallback default list if endpoint temporarily fails
        const fallbackPages: RolePermissionPageItem[] = this.getFallbackClientPages().map((cp) => ({
          page_id: cp.id || cp.page_id || 1,
          page_name: cp.name || cp.page_name || '',
          page_key: cp.page_key || cp.slug || '',
          url: cp.route || cp.url || '',
          parent_id: cp.parent_id || null,
          can_view: false,
          can_create: false,
          can_edit: false,
          can_delete: false,
        }));
        return of({
          role: { id: numId, policy_name: `Role #${numId}` },
          allowed_count: 0,
          total_pages: fallbackPages.length,
          pages: fallbackPages,
        });
      })
    );
  }

  /**
   * 5. POST /client_role_permissions_bulk
   * {
   *   "role_id": 5,
   *   "permissions": [
   *     { "page_id": 1, "can_view": true,  "can_create": true,  "can_edit": true,  "can_delete": false },
   *     { "page_id": 2, "can_view": false, "can_create": false, "can_edit": false, "can_delete": false }
   *   ]
   * }
   * Note: Send the WHOLE set back, not just the ones that changed.
   */
  updateClientRolePermissionsBulk(
    roleId: number | string,
    permissions: Array<{
      page_id: number;
      can_view: boolean;
      can_create?: boolean;
      can_edit?: boolean;
      can_delete?: boolean;
    }>
  ): Observable<any> {
    const payload = {
      role_id: Number(roleId),
      permissions: permissions.map((p) => {
        // A page that cannot be viewed cannot be acted on either — normalize here so
        // the backend never receives an impossible combination.
        const canView = !!p.can_view;
        return {
          page_id: Number(p.page_id),
          can_view: canView,
          can_create: canView && !!p.can_create,
          can_edit: canView && !!p.can_edit,
          can_delete: canView && !!p.can_delete,
        };
      }),
    };
    console.log(`[SubuserPermissionsService] POST /client_role_permissions_bulk payload:`, payload);
    return this.pagesService.create('CLIENT_ROLE_PERMISSIONS_BULK', payload).pipe(
      catchError(() => this.pagesService.update('CLIENT_ROLE_PERMISSIONS_BULK', payload)),
      tap((res) => console.log('[SubuserPermissionsService] updateClientRolePermissionsBulk success:', res))
    );
  }

  /**
   * 6. GET /get_sub_users
   * Fetches sub-users belonging to client admin.
   */
  getSubUsersByParent(parentId: number | string, page?: string | number, perPage?: string | number): Observable<any> {
    const queryParams: any = {
      parent_id: Number(parentId) || 62,
    };
    if (page !== undefined && page !== null && page !== '') {
      queryParams.page = String(page);
    }
    if (perPage !== undefined && perPage !== null && perPage !== '') {
      queryParams.per_page = String(perPage);
    }
    console.log('[SubuserPermissionsService] GET /get_sub_users query:', queryParams);
    return this.pagesService.getAll('GET_SUB_USERS_BY_PARENT', queryParams).pipe(
      timeout(4500),
      map((res: any) => {
        console.log('[SubuserPermissionsService] GET /get_sub_users response:', res);
        return res;
      }),
      catchError((err) => {
        console.warn('[SubuserPermissionsService] Failed to fetch sub-users:', err);
        return of({ items: [], data: [] });
      })
    );
  }

  /**
   * 7. POST /assign_subuser_role
   * Assigns a role to a sub-user.
   * Send multi-alias payload to ensure Xano endpoint binds correctly.
   */
  assignSubUserRole(subUserId: number | string, roleId: number | string): Observable<any> {
    const numSubUserId = Number(subUserId);
    const numRoleId = Number(roleId);

    const payload = {
      sub_user_id: numSubUserId,
      subuser_id: numSubUserId,
      user_id: numSubUserId,
      id: numSubUserId,
      role_id: numRoleId,
      client_role_id: numRoleId,
      client_roles_id: numRoleId,
    };
    console.log('[SubuserPermissionsService] POST /assign_subuser_role payload:', payload);

    return this.pagesService.create('ASSIGN_SUBUSER_ROLE', payload).pipe(
      catchError(() => this.pagesService.update('ASSIGN_SUBUSER_ROLE', payload)),
      tap((res) => {
        console.log('[SubuserPermissionsService] assignSubUserRole success:', res);
        // Cache ONLY after the backend accepted it. Writing before the request
        // meant a rejected assignment still counted, so the Roles screen showed
        // members the server did not have.
        this.saveSubUserRoleToCache(numSubUserId, numRoleId);
        this.policySummarySubject.next(null);
      })
    );
  }

  /**
   * Helper to retrieve assigned role ID for a sub-user.
   * Checks API object variations (role_id, client_role_id, client_roles_id, assigned_role, role name)
   * and falls back to persistent local storage cache.
   */
  getSubUserAssignedRole(user: any, rolesList?: any[]): number {
    if (!user) return 0;

    // 1. Direct candidate fields from backend user object
    const rawCandidates = [
      user.role_id,
      user.client_role_id,
      user.client_roles_id,
      user.roles_id,
      user.assigned_role_id,
      user.assigned_role?.id,
      user.assigned_role?.role_id,
      user.role?.id,
      user.client_role?.id,
      user.client_roles?.id,
    ];

    for (const val of rawCandidates) {
      if (val !== undefined && val !== null && !isNaN(Number(val)) && Number(val) > 0) {
        return Number(val);
      }
    }

    // 2. If user.role is a string role name (e.g. "Directory" or "Roles Management")
    if (typeof user.role === 'string' && rolesList && rolesList.length > 0) {
      const cleanRole = user.role.toLowerCase().trim();
      const matched = rolesList.find(
        (r) => r.policy_name && r.policy_name.toLowerCase().trim() === cleanRole
      );
      if (matched && matched.id) {
        return Number(matched.id);
      }
    }

    // 3. Fallback to persistent localStorage cache
    const userId = Number(user.id ?? user.sub_user_id ?? user.user_id ?? 0);
    if (userId > 0) {
      const cachedMap = this.getCachedSubUserRoles();
      if (cachedMap && cachedMap[userId] !== undefined) {
        return Number(cachedMap[userId]);
      }
    }

    return 0;
  }

  getCachedSubUserRoles(): Record<number, number> {
    try {
      const stored = localStorage.getItem('luqta_assigned_subuser_roles');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  saveSubUserRoleToCache(subUserId: number, roleId: number): void {
    try {
      const cachedMap = this.getCachedSubUserRoles();
      if (roleId > 0) {
        cachedMap[subUserId] = Number(roleId);
      } else {
        delete cachedMap[subUserId];
      }
      localStorage.setItem('luqta_assigned_subuser_roles', JSON.stringify(cachedMap));
    } catch {
      // ignore
    }
  }

  /**
   * Canonical client-portal page list — mirrors the rows seeded in the backend
   * `client_pages` table (same page_key / url / parent).
   *
   * These ids are placeholders used ONLY when the API returns no pages; they are
   * NOT real database ids, so permissions saved off this list will be rejected by
   * /client_role_permissions_bulk. It exists to keep the sidebar usable, not to
   * drive saves.
   *
   * page_key is snake_case and must match the backend exactly — it is what
   * /my_permissions returns in allowed_keys and what the sidebar filters on.
   */
  public getFallbackClientPages(): ClientPageItem[] {
    const pages: Array<[number, string, string, string, number | null, string, string]> = [
      // id, page_name, page_key, url, parent_id, group, icon
      [1, 'Quick Actions', 'quick_actions', '/home/quick-actions', null, 'Main', 'fa-solid fa-bolt'],
      [2, 'Dashboard', 'dashboard', '/home', null, 'Main', 'fa-solid fa-gauge-high'],
      [3, 'AI Insights', 'ai_insights', '/home/ai-insight', null, 'Main', 'fa-solid fa-wand-magic-sparkles'],

      [4, 'Engagements', 'engagements', '/home/contests', null, 'Engagement Management', 'fa-solid fa-trophy'],
      [5, 'Quiz', 'quiz', '/home/quiz', null, 'Engagement Management', 'fa-solid fa-circle-question'],
      [6, 'Participants', 'participants', '/home/participants', null, 'Engagement Management', 'fa-solid fa-users'],
      [7, 'Web QR', 'web_qr', '/home/contests/web-qr', 4, 'Engagement Management', 'fa-solid fa-qrcode'],
      [24, 'Branches', 'branches', '/home/branches', null, 'Engagement Management', 'fa-solid fa-store'],

      [8, 'Developer', 'developer', '/home/developer', null, 'Technical & SDK', 'fa-solid fa-code'],
      [9, 'SDK Docs', 'sdk_documentation', '/home/sdk-documentation', null, 'Technical & SDK', 'fa-solid fa-book'],
      [10, 'SDK Applications', 'sdk_applications', '/home/sdk-applications', 9, 'Technical & SDK', 'fa-solid fa-cubes'],
      [11, 'Activity Logs', 'activity_logs', '/home/activity-logs', null, 'Technical & SDK', 'fa-solid fa-clock-rotate-left'],
      [12, 'WhatsApp Logs', 'whatsapp_logs', '/home/whatsapp-logs', null, 'Technical & SDK', 'fa-brands fa-whatsapp'],
      [13, 'WhatsApp', 'whatsapp_conversation_log', '/home/whatsapp', null, 'Technical & SDK', 'fa-solid fa-comments'],
      [14, 'Branding', 'branding', '/home/branding', null, 'Technical & SDK', 'fa-solid fa-palette'],
      [15, '3D Model', 'model_3d', '/home/3d-model', null, 'Technical & SDK', 'fa-solid fa-cube'],

      [16, 'Subscription', 'subscription', '/home/subscription', null, 'Account', 'fa-solid fa-credit-card'],
      [17, 'Billing & Payment', 'billing', '/home/billing', null, 'Account', 'fa-solid fa-file-invoice-dollar'],
      [18, 'User Management', 'user_management', '/home/user-managment', null, 'Account', 'fa-solid fa-user-gear'],
      [19, 'Policy Management', 'policy_management', '/home/policy-management', null, 'Account', 'fa-solid fa-shield-halved'],
      [20, 'Roles', 'roles', '/home/roles', 19, 'Account', 'fa-solid fa-users-gear'],
      [21, 'Roles Management', 'roles_management', '/home/roles-management', 19, 'Account', 'fa-solid fa-sliders'],
      [24, 'Pages', 'pages', '/home/client-pages', 19, 'Account', 'fa-solid fa-file-lines'],
      [22, 'Settings', 'settings', '/home/profile', null, 'Account', 'fa-solid fa-gear'],
      [23, 'Updates', 'updates', '/home/updates', null, 'Account', 'fa-solid fa-bell'],
    ];

    return pages.map(([id, name, key, url, parentId, group, icon]) => ({
      id,
      page_id: id,
      name,
      page_name: name,
      // slug is kept equal to page_key so the two never disagree — an older build
      // let a differing slug silently override the key.
      page_key: key,
      slug: key,
      route: url,
      url,
      parent_id: parentId,
      group,
      icon,
      can_view: true,
    }));
  }

  clear(): void {
    this.myPermissionsSubject.next(null);
    localStorage.removeItem('luqta_my_permissions');
    // Role assignments are per company account — leaving them behind made a new
    // login inherit the previous account's role counts.
    localStorage.removeItem('luqta_assigned_subuser_roles');
    this.cachedClientPages = null;
    this.policySummarySubject.next(null);
    this.policySummaryInFlight = null;
  }
}
